import type {
  AuthUser,
  InventoryRow,
  ShipmentRequest,
  ShipmentStatus,
} from "@apple-bee/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  Clock3,
  PackageCheck,
  Plus,
  Send,
  X,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, LoadingBlock, Message } from "../components/Feedback";
import { Modal } from "../components/Modal";
import { dateFormatter, dateTimeFormatter, formatKg } from "../format";

const statusLabels: Record<ShipmentStatus, string> = {
  DRAFT: "Черновик",
  PENDING_APPROVAL: "На согласовании",
  APPROVED: "Одобрено",
  REJECTED: "Отклонено",
  SHIPPED: "Отгружено",
  ACCEPTED: "Принято",
  CANCELLED: "Отменено",
};

type Customer = { id: string; name: string };

export function ShipmentsPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus | "ALL">("ALL");
  const shipmentsQuery = useQuery({
    queryKey: ["shipments"],
    queryFn: () =>
      api<{ shipments: ShipmentRequest[] }>("/api/shipments"),
  });
  const inventoryQuery = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api<{ inventory: InventoryRow[] }>("/api/inventory"),
  });
  const customersQuery = useQuery({
    queryKey: ["customers"],
    queryFn: () => api<{ customers: Customer[] }>("/api/reference/customers"),
  });

  const canCreate = user.roles.some((role) =>
    ["OPERATIONS", "LOGISTICS"].includes(role),
  );
  const filtered = useMemo(
    () =>
      (shipmentsQuery.data?.shipments ?? []).filter(
        (shipment) =>
          statusFilter === "ALL" || shipment.status === statusFilter,
      ),
    [shipmentsQuery.data, statusFilter],
  );

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["shipments"] }),
      queryClient.invalidateQueries({ queryKey: ["inventory"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);
  }

  if (shipmentsQuery.isLoading) {
    return <LoadingBlock label="Загружаем заявки…" />;
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Контроль исполнения</p>
          <h2>Заявки и отгрузки</h2>
          <p>
            Товар резервируется только после одобрения CEO и списывается при
            фактической отгрузке.
          </p>
        </div>
        {canCreate ? (
          <button
            className="button button--primary"
            type="button"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={18} />
            Новая заявка
          </button>
        ) : null}
      </section>

      <section className="status-tabs" aria-label="Фильтр заявок">
        {(
          [
            ["ALL", "Все"],
            ["PENDING_APPROVAL", "На согласовании"],
            ["APPROVED", "Одобрено"],
            ["SHIPPED", "Отгружено"],
            ["ACCEPTED", "Закрыто"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={statusFilter === value ? "is-active" : ""}
            onClick={() => setStatusFilter(value)}
          >
            {label}
          </button>
        ))}
      </section>

      {filtered.length ? (
        <section className="shipment-list">
          {filtered.map((shipment) => (
            <ShipmentCard
              key={shipment.id}
              shipment={shipment}
              user={user}
              onChanged={refresh}
            />
          ))}
        </section>
      ) : (
        <section className="panel">
          <EmptyState
            title="Заявок в этом статусе нет"
            description={
              statusFilter === "ALL"
                ? "Первая заявка появится после выбора клиента и складской партии."
                : "Выберите другой статус или создайте новую заявку."
            }
          />
        </section>
      )}

      {createOpen ? (
        <CreateShipmentModal
          customers={customersQuery.data?.customers ?? []}
          inventory={(inventoryQuery.data?.inventory ?? []).filter(
            (row) => row.availableKg > 0,
          )}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function ShipmentCard({
  shipment,
  user,
  onChanged,
}: {
  shipment: ShipmentRequest;
  user: AuthUser;
  onChanged: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const canApprove = user.roles.includes("CEO");
  const canShip = user.roles.some((role) =>
    ["LOGISTICS", "STOREKEEPER"].includes(role),
  );
  const canAccept = user.roles.some((role) =>
    ["OPERATIONS", "LOGISTICS"].includes(role),
  );

  const actionMutation = useMutation({
    mutationFn: async ({
      action,
      body,
    }: {
      action: "approve" | "reject" | "ship" | "accept";
      body?: Record<string, unknown>;
    }) =>
      api(`/api/shipments/${shipment.id}/${action}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),
    onSuccess: onChanged,
    onError: (caught) => setError(errorMessage(caught)),
  });

  async function act(
    action: "approve" | "reject" | "ship" | "accept",
    body?: Record<string, unknown>,
  ) {
    setBusy(action);
    setError(null);
    await actionMutation.mutateAsync({ action, body }).catch(() => undefined);
    setBusy(null);
  }

  async function reject() {
    const reason = window.prompt("Причина отказа (не менее 5 символов):");
    if (reason) await act("reject", { reason });
  }

  const total = shipment.lines.reduce((sum, line) => sum + line.quantityKg, 0);

  return (
    <article className="shipment-card">
      <header className="shipment-card__header">
        <div>
          <span className={`status-pill status-pill--${shipment.status.toLowerCase()}`}>
            {statusLabels[shipment.status]}
          </span>
          <h3>{shipment.number}</h3>
          <p>{shipment.customerName}</p>
        </div>
        <div className="shipment-card__date">
          <span>Плановая дата</span>
          <strong>{dateFormatter.format(new Date(shipment.requestedDate))}</strong>
        </div>
      </header>
      <div className="shipment-card__lines">
        {shipment.lines.map((line) => (
          <div key={line.id}>
            <span>
              <strong>{line.productName}</strong>
              <small>
                {line.lotCode} · {line.warehouseName}
              </small>
            </span>
            <strong>{formatKg(line.quantityKg)}</strong>
          </div>
        ))}
      </div>
      <footer className="shipment-card__footer">
        <div>
          <span>Итого</span>
          <strong>{formatKg(total)}</strong>
          <small>
            Создал(а) {shipment.createdByName} ·{" "}
            {dateTimeFormatter.format(new Date(shipment.createdAt))}
          </small>
        </div>
        <div className="shipment-card__actions">
          {shipment.status === "PENDING_APPROVAL" && canApprove ? (
            <>
              <button
                className="button button--danger-ghost"
                type="button"
                onClick={() => void reject()}
                disabled={Boolean(busy)}
              >
                <X size={17} /> Отклонить
              </button>
              <button
                className="button button--primary"
                type="button"
                onClick={() => void act("approve")}
                disabled={Boolean(busy)}
              >
                <Check size={17} /> {busy === "approve" ? "Одобряем…" : "Одобрить"}
              </button>
            </>
          ) : null}
          {shipment.status === "APPROVED" && canShip ? (
            <button
              className="button button--primary"
              type="button"
              onClick={() => void act("ship")}
              disabled={Boolean(busy)}
            >
              <Send size={17} /> {busy === "ship" ? "Проводим…" : "Отгрузить"}
            </button>
          ) : null}
          {shipment.status === "SHIPPED" && canAccept ? (
            <button
              className="button button--primary"
              type="button"
              onClick={() => void act("accept")}
              disabled={Boolean(busy)}
            >
              <CheckCircle2 size={17} /> Закрыть приёмку
            </button>
          ) : null}
          {!(
            (shipment.status === "PENDING_APPROVAL" && canApprove) ||
            (shipment.status === "APPROVED" && canShip) ||
            (shipment.status === "SHIPPED" && canAccept)
          ) ? (
            <span className="quiet-status">
              {shipment.status === "PENDING_APPROVAL" ? (
                <Clock3 size={17} />
              ) : (
                <PackageCheck size={17} />
              )}
              Действий от вашей роли нет
            </span>
          ) : null}
        </div>
      </footer>
      {error ? <Message tone="error">{error}</Message> : null}
    </article>
  );
}

function CreateShipmentModal({
  customers,
  inventory,
  onClose,
  onCreated,
}: {
  customers: Customer[];
  inventory: InventoryRow[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? "");
  const [requestedDate, setRequestedDate] = useState(
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([
    { balanceId: inventory[0]?.balanceId ?? "", quantityKg: "" },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function updateLine(
    index: number,
    field: "balanceId" | "quantityKg",
    value: string,
  ) {
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/shipments", {
        method: "POST",
        body: JSON.stringify({
          customerId,
          requestedDate,
          notes: notes || undefined,
          lines,
        }),
      });
      await onCreated();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Новая заявка на отгрузку"
      subtitle="После создания заявка будет направлена CEO на согласование."
      onClose={onClose}
    >
      <form className="modal__body form-grid" onSubmit={(event) => void submit(event)}>
        <label>
          Клиент
          <select value={customerId} onChange={(event) => setCustomerId(event.target.value)} required>
            <option value="">Выберите клиента</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Плановая дата
          <input type="date" value={requestedDate} onChange={(event) => setRequestedDate(event.target.value)} required />
        </label>
        <div className="span-2 line-builder">
          <div className="line-builder__heading">
            <strong>Позиции заявки</strong>
            <button
              className="text-button"
              type="button"
              onClick={() => setLines((current) => [...current, { balanceId: "", quantityKg: "" }])}
            >
              <Plus size={15} /> Добавить позицию
            </button>
          </div>
          {lines.map((line, index) => {
            const selected = inventory.find((row) => row.balanceId === line.balanceId);
            return (
              <div className="line-builder__row" key={`${index}-${line.balanceId}`}>
                <label>
                  Партия и склад
                  <select value={line.balanceId} onChange={(event) => updateLine(index, "balanceId", event.target.value)} required>
                    <option value="">Выберите остаток</option>
                    {inventory.map((row) => (
                      <option key={row.balanceId} value={row.balanceId}>
                        {row.productName} · {row.lotCode} · {row.warehouseName} ({formatKg(row.availableKg)})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Количество, кг
                  <input
                    type="number"
                    min="0.001"
                    max={selected?.availableKg}
                    step="0.001"
                    value={line.quantityKg}
                    onChange={(event) => updateLine(index, "quantityKg", event.target.value)}
                    required
                  />
                </label>
                {lines.length > 1 ? (
                  <button
                    className="icon-button line-builder__remove"
                    type="button"
                    onClick={() => setLines((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    aria-label="Удалить позицию"
                  >
                    <X size={17} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        <label className="span-2">
          Комментарий
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Условия поставки или важная информация" />
        </label>
        {error ? (
          <div className="span-2">
            <Message tone="error">{error}</Message>
          </div>
        ) : null}
        <div className="modal__actions span-2">
          <button className="button button--secondary" type="button" onClick={onClose}>
            Отмена
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Создаём…" : "Отправить на согласование"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

